// hardcoded-ui-text: allow — the language selector lists endonyms: each language written in its
// own language. The rest of this screen goes through t().
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, UserPlus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { getErrorMessage } from '@/lib/error-utils';

const createPasswordSchema = (t: (key: string) => string) => z.object({
  password: z.string()
    .min(8, t('validation:passwordMin8'))
    .regex(/[A-Z]/, t('validation:passwordUppercase'))
    .regex(/[a-z]/, t('validation:passwordLowercase'))
    .regex(/[0-9]/, t('validation:passwordNumber'))
    // eslint-disable-next-line no-useless-escape
    .regex(/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/, t('validation:passwordSpecialChar')),
  confirmPassword: z.string(),
  phone_number: z.string().min(10, t('validation:phoneRequired')),
  language: z.string().default('es'),
  timezone: z.string().default('America/Santiago'),
}).refine((data) => data.password === data.confirmPassword, {
  message: t('validation:passwordMatch'),
  path: ["confirmPassword"],
});

export default function ActivateAccount() {
  const { t } = useTranslation(['auth', 'validation', 'notifications']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<{ role: string; [key: string]: unknown } | null>(null);
  // A dead invitation link is a dead end unless the invitee can ask for a new
  // one right here: the login they are offered is one they cannot pass yet,
  // because activating IS what sets their password (P-04).
  const [requestEmail, setRequestEmail] = useState('');
  const [requestSending, setRequestSending] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordSchema = createPasswordSchema(t);
  type PasswordFormData = z.infer<typeof passwordSchema>;

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
      phone_number: '',
      language: 'es',
      timezone: 'America/Santiago',
    },
  });

  const password = form.watch('password');

  useEffect(() => {
    if (!token) {
      setError(t('auth:invalidInvitationToken'));
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const { data, error } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('invitation_token', token)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .single();

      if (error || !data) {
        setError(t('auth:invalidOrExpiredInvitation'));
      } else {
        setInvitation(data);
      }
    } catch (err) {
      setError(t('auth:invitationValidationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (data: PasswordFormData) => {
    if (!invitation) return;

    setActivating(true);
    try {
      // Call Edge Function to handle activation with Auth cleanup
      const { data: result, error } = await supabase.functions.invoke('invite-manager', {
        body: {
          action: 'activate',
          invitation_token: token,
          password: data.password,
          phone_number: data.phone_number,
          language: data.language,
          timezone: data.timezone,
        },
      });

      if (error) {
        throw new Error(error.message || t('auth:activationFailedGeneric'));
      }

      if (!result?.success) {
        throw new Error(result?.error || t('auth:activationFailed'));
      }

      console.log('Activation successful:', result);

      toast.success(t('notifications:activationSuccess'), {
        description: t('notifications:activationSuccessDesc'),
      });

      // Redirect to login page
      navigate('/auth?message=Account activated successfully');
    } catch (err: unknown) {
      console.error('Activation error:', err);
      toast.error(t('notifications:activationError'), {
        description: getErrorMessage(err) || t('notifications:tryAgainLater'),
      });
    } finally {
      setActivating(false);
    }
  };

  const handleRequestNewLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestSending(true);
    setRequestError(null);
    try {
      // An invitation is a recovery link underneath (invite-platform-admin mints
      // it with generateLink type 'recovery'), so asking for a recovery link is
      // literally asking for a fresh invitation.
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(requestEmail, {
        redirectTo: `${window.location.origin}/activate-account`,
      });
      if (resetError) setRequestError(resetError.message);
      else setRequestSent(true);
    } catch {
      setRequestError(t('auth:unexpectedErrorRetry'));
    } finally {
      setRequestSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center p-6">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>{t('auth:validatingInvitation')}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>{t('auth:invalidInvitation')}</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            {requestSent ? (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{t('auth:resendConfirmationSent')}</AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                {requestError && (
                  <Alert variant="destructive">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription>{requestError}</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={handleRequestNewLink} className="space-y-4">
                  <div className="space-y-2">
                    <FormLabel htmlFor="request-email">{t('auth:email')}</FormLabel>
                    <Input
                      id="request-email"
                      type="email"
                      placeholder={t('auth:emailPlaceholder')}
                      value={requestEmail}
                      onChange={(ev) => setRequestEmail(ev.target.value)}
                      required
                      disabled={requestSending}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={requestSending || !requestEmail}>
                    {requestSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('auth:requestNewInvitation')}
                  </Button>
                </form>
                <Button
                  onClick={() => navigate('/auth')}
                  variant="link"
                  className="w-full text-xs underline"
                >
                  {t('auth:goToLogin')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <UserPlus className="h-12 w-12 text-primary mx-auto mb-2" />
          <CardTitle>{t('auth:activateAccount')}</CardTitle>
          <CardDescription>
            {t('auth:activateWelcome', { name: invitation?.full_name || '' })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitation && (
            <Alert className="mb-6">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                {t('auth:addingAsRole', { role: invitation.role })}
              </AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleActivate)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth:password')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder={t('auth:passwordPlaceholder')}
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-600" />

                    {/* Password Requirements Checklist */}
                    {password && (
                      <div className="mt-2 space-y-1 text-sm">
                        <div className={password.length >= 8 ? "text-green-600" : "text-red-500"}>
                          {password.length >= 8 ? "✓" : "✗"} {t('validation:passwordMin8')}
                        </div>
                        <div className={/[A-Z]/.test(password) ? "text-green-600" : "text-red-500"}>
                          {/[A-Z]/.test(password) ? "✓" : "✗"} {t('validation:passwordUppercase')}
                        </div>
                        <div className={/[a-z]/.test(password) ? "text-green-600" : "text-red-500"}>
                          {/[a-z]/.test(password) ? "✓" : "✗"} {t('validation:passwordLowercase')}
                        </div>
                        <div className={/[0-9]/.test(password) ? "text-green-600" : "text-red-500"}>
                          {/[0-9]/.test(password) ? "✓" : "✗"} {t('validation:passwordNumber')}
                        </div>
                        {/* eslint-disable-next-line no-useless-escape */}
                        <div className={/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) ? "text-green-600" : "text-red-500"}>
                          {/* eslint-disable-next-line no-useless-escape */}
                          {/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password) ? "✓" : "✗"} {t('validation:passwordSpecialChar')}
                        </div>
                      </div>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth:confirmPassword')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={t('auth:confirmPasswordPlaceholder')}
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-600" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth:phoneNumber')} *</FormLabel>
                    <FormControl>
                      <PhoneInput
                        international
                        defaultCountry="CL"
                        value={field.value}
                        onChange={field.onChange}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="+56 9 1234 5678"
                      />
                    </FormControl>
                    <FormMessage className="text-red-600" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth:preferredLanguage')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('auth:selectLanguage')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="es">Español</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="pt">Português</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-red-600" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="timezone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth:timezone')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('auth:selectTimezone')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="America/Santiago">{t('auth:timezoneOptions.santiago')}</SelectItem>
                        <SelectItem value="America/Argentina/Buenos_Aires">{t('auth:timezoneOptions.buenosAires')}</SelectItem>
                        <SelectItem value="America/Sao_Paulo">{t('auth:timezoneOptions.saoPaulo')}</SelectItem>
                        <SelectItem value="America/Mexico_City">{t('auth:timezoneOptions.mexicoCity')}</SelectItem>
                        <SelectItem value="America/Bogota">{t('auth:timezoneOptions.bogota')}</SelectItem>
                        <SelectItem value="America/Lima">{t('auth:timezoneOptions.lima')}</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage className="text-red-600" />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={activating}
              >
                {activating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('auth:activatingAccount')}
                  </>
                ) : (
                  t('auth:activateAccount')
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}